import { useState } from "react";
import { useMutation } from "react-query";
import { usePageTracking } from "react-event-tracker";
import { Button } from "basis";
import { Loading } from "components/Loading/Loading";
import { PasswordInput } from "components/PasswordInput/PasswordInput";
import { OtpInput, OTP_ERRORS } from "components/OtpInput/OtpInput";
import { useAuthentication } from "library/hooks/auth/useAuthentication";
import { putPasswordRecovery, postPasswordRecovery } from "library/api";
import styles from "./password-recovery.module.scss";

export const ResetPasswordOtp = ({ params, setScreen }) => {
  usePageTracking({
    page: {
      pageType: "input-page",
      siteSection: "sign-in",
      siteSubSection: "",
      pageTitle: "new-password",
    },
    user: {
      authenticationStatus: "not logged in",
    },
  });
  const [otp, setOtp] = useState({ value: null, success: false });
  const [password, setPassword] = useState({ value: null, success: false });
  const [otpError, setOtpError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const { loginUser } = useAuthentication();
  const { email } = params;
  const error = otpError || passwordError;
  const setError = ({ code, message }) => {
    if (OTP_ERRORS[code]) {
      setOtpError(OTP_ERRORS[code]);
    } else {
      setPasswordError(message);
    }
  };

  const loginMutation = useMutation(loginUser, {
    onSettled: () => setScreen("ResetPasswordSuccess"),
  });
  const verifyPasswordResetMutation = useMutation(putPasswordRecovery, {
    onSuccess: () =>
      loginMutation.mutate({
        id: email.value,
        password: password.value,
      }),
    onError: setError,
  });
  const startPasswordResetMutation = useMutation(postPasswordRecovery, {
    onError: setError,
  });

  const isLoading =
    loginMutation.isLoading || verifyPasswordResetMutation.isLoading;

  return isLoading ? (
    <div className={styles.loaderWrapper}>
      <Loading />
    </div>
  ) : (
    <div className={styles.resetPasswordWrapper}>
      <h1 className="h4">Reset password</h1>
      <form
        method="post"
        onSubmit={(e) => {
          e.preventDefault();
          verifyPasswordResetMutation.mutate({
            passCode: otp.value,
            newPassword: password.value,
            id: email.value,
          });
        }}
      >
        <p>
          We've sent a 6 digit code to your email address. Enter it below, then
          set your new password.
        </p>
        {error && (
          <div className={`body02 ${styles.error}`} data-trackid="otp-error">
            {error}
          </div>
        )}
        <OtpInput
          resendOtp={() =>
            startPasswordResetMutation.mutate({ id: email.value })
          }
          inputStyle={styles.otpInput}
          onChange={(code) => {
            setOtp(code);
            setOtpError(false);
            setPasswordError(false);
          }}
          disabled={isLoading}
        />
        <div className={styles.password}>
          <PasswordInput
            label="Your new password"
            evaluator
            onChange={(pass) => {
              setPassword(pass);
              setOtpError(false);
              setPasswordError(false);
            }}
            disabled={isLoading}
          />
        </div>
        <Button
          width="176"
          type="submit"
          disabled={!otp.success || !password.success || isLoading}
        >
          Set password
        </Button>
      </form>
    </div>
  );
};
